import { z } from "zod";

export const DataPlacementSchema = z.enum(["local", "cloud", "hybrid"]);

export const UserContextSchema = z.object({
  subject: z.string().min(1),
  issuer: z.string().url().optional(),
  accountId: z.string().uuid().optional(),
  email: z.string().email().optional(),
  roles: z.array(z.string().min(1)).default([]),
  authenticationMethods: z.array(z.string().min(1)).default([]),
  authenticatedAt: z.string().datetime().optional(),
  dataPlacement: DataPlacementSchema.default("hybrid"),
  homeRegion: z.string().min(1).optional(),
  dataPartition: z.string().min(1).optional(),
  placementVersion: z.number().int().positive().optional(),
  placementState: z.enum(["active", "migrating"]).optional(),
});

export const AccountProfileSchema = z.object({
  user_id: z.string().uuid(),
  username: z.string().min(1),
  email: z.string().email().nullable(),
  display_name: z.string().min(1),
  picture_url: z.string().url().nullable(),
  preferred_language: z.enum(["zh-CN", "en-US"]),
});

export const AccountProfileUpdateSchema = z
  .object({
    display_name: z.string().trim().min(1).max(64).optional(),
    preferred_language: z.enum(["zh-CN", "en-US"]).optional(),
  })
  .refine(
    (value) =>
      value.display_name !== undefined ||
      value.preferred_language !== undefined,
    {
      message: "At least one profile field must be provided",
    },
  );

export const AccountStatusSchema = z.object({
  auth_state: z.enum(["guest", "registered"]),
  user: AccountProfileSchema.nullable(),
  capabilities: z.object({
    sync_enabled: z.boolean(),
    server_persistence_enabled: z.boolean(),
    rate_limited: z.literal(true),
  }),
  external_user_management: z.record(z.string(), z.string()),
});

export const AuthPublicConfigSchema = z.object({
  domain: z.string().min(1),
  client_id: z.string().min(1),
  audience: z.string().min(1),
  scope: z.string().min(1),
});

export const IdentityProviderSchema = z.object({
  provider: z.literal("auth0"),
  status: z.literal("enabled"),
  protocol: z.literal("oauth2-oidc-pkce"),
  subject_claim: z.literal("sub"),
  username_claim: z.literal("nickname"),
  email_claim: z.literal("email"),
});
export const IdentityProvidersSchema = z.array(IdentityProviderSchema);

export const ExternalAccountProviderSchema = z.enum(["lark"]);

export const ExternalAccountBindingSchema = z.object({
  provider: ExternalAccountProviderSchema,
  configured: z.boolean(),
  connection: z
    .object({
      display_name: z.string().min(1),
      email: z.string().email().nullable(),
      avatar_url: z.string().url().nullable(),
      connected_at: z.iso.datetime(),
      updated_at: z.iso.datetime(),
    })
    .nullable(),
});
export const ExternalAccountBindingsSchema = z.array(
  ExternalAccountBindingSchema,
);

export const ExternalAccountAuthorizationRequestSchema = z.object({
  return_uri: z.string().url().max(2048),
});

export const ExternalAccountAuthorizationResponseSchema = z.object({
  authorization_url: z.string().url(),
  expires_at: z.iso.datetime(),
});

export type DataPlacement = z.infer<typeof DataPlacementSchema>;
export type UserContext = z.infer<typeof UserContextSchema>;
export type AccountProfile = z.infer<typeof AccountProfileSchema>;
export type AccountProfileUpdate = z.infer<typeof AccountProfileUpdateSchema>;
export type AuthPublicConfig = z.infer<typeof AuthPublicConfigSchema>;
export type ExternalAccountProvider = z.infer<
  typeof ExternalAccountProviderSchema
>;
export type ExternalAccountBinding = z.infer<
  typeof ExternalAccountBindingSchema
>;
export type ExternalAccountAuthorizationRequest = z.infer<
  typeof ExternalAccountAuthorizationRequestSchema
>;
export type ExternalAccountAuthorizationResponse = z.infer<
  typeof ExternalAccountAuthorizationResponseSchema
>;
